import { stripLocalePrefix } from "@/i18n/routing"

// ============================================================
// Where "back" goes, and when it should be offered at all.
// ------------------------------------------------------------
// The shell has no navigation bar of its own, so until now the only way out of
// a detail page was the top-left control — which is a SHORTCUT, not a back
// button. From a shop page it read "Dashboard" and threw you to the dashboard,
// so there was no way back to the listings you were browsing.
//
// Two rules, and the second one is the one people forget.
//
//   1. Offer Back below a root, not at one. A root screen has nothing
//      meaningful behind it, and the shortcut is worth more there. Same reason
//      a tab's root screen in any iOS app shows no back chevron.
//
//   2. Back must never dead-end. router.back() is right only when the person
//      actually arrived from somewhere in the app. A push notification can
//      open a shop page cold, with an empty history, and a Back control that
//      does nothing is worse than no Back control. Every non-root route
//      therefore declares a PARENT to fall back to.
// ============================================================

/** Screens with nothing meaningful behind them. */
const ROOTS: ReadonlyArray<string> = [
  "/",
  "/dashboard",
  "/dashboard/barber",
  "/dashboard/owner",
  "/dashboard/client",
  "/dashboard/messages",
  "/dashboard/owner/messages",
  "/listings",
  "/search",
  "/hiring",
  "/barbers",
]

/**
 * Longest-prefix parents. Order does not matter — the longest match wins — so
 * a new entry can be dropped in anywhere.
 *
 * A pattern ending in `/` matches anything BELOW that path, which is what makes
 * one line cover every id: "/listings/" sends /listings/anything to /listings.
 */
const PARENTS: ReadonlyArray<readonly [string, string]> = [
  ["/listings/", "/listings"],
  ["/barbers/", "/barbers"],
  ["/hiring/", "/hiring"],
  ["/book/", "/listings"],
  ["/dashboard/owner/shop/", "/dashboard/owner"],
  ["/dashboard/barber/", "/dashboard/barber"],
  ["/dashboard/owner/", "/dashboard/owner"],
  ["/dashboard/client/", "/dashboard/client"],
  ["/dashboard/account", "/dashboard/barber"],
  ["/dashboard/", "/dashboard/barber"],
]

/** True when this path is a screen with nothing behind it. */
export function isRootPath(rawPathname: string | null | undefined): boolean {
  if (!rawPathname) return true
  const path = stripLocalePrefix(rawPathname).replace(/\/$/, "") || "/"
  return ROOTS.includes(path)
}

/**
 * Where Back goes when there is no in-app history to pop.
 *
 * Returns null only for a path with no declared parent, and the caller then
 * shows no Back at all rather than a control that guesses.
 */
export function parentPath(rawPathname: string | null | undefined): string | null {
  if (!rawPathname) return null
  const path = stripLocalePrefix(rawPathname).replace(/\/$/, "") || "/"
  if (isRootPath(path)) return null

  let best: string | null = null
  let bestLength = -1
  for (const [prefix, parent] of PARENTS) {
    const matches = prefix.endsWith("/") ? path.startsWith(prefix) : path === prefix || path.startsWith(`${prefix}/`)
    if (matches && prefix.length > bestLength) {
      best = parent
      bestLength = prefix.length
    }
  }
  // Never offer to go back to where we already are.
  return best === path ? null : best
}
